// frontend/src/components/admin/AssociationCard.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../utils/constants';
import api from '../../services/api';
import { connectSocket, joinAssociation, onAssociationUpdate, onAssociationControl } from '../../services/socket';
import Modal from '../common/Modal';
import AcControlPanel from './AcControlPanel';

const PRESENCE_LABELS = {
  presence: { label: 'Présence détectée', className: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  move: { label: 'Mouvement détecté', className: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500 animate-pulse' },
  none: { label: 'Aucune présence', className: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  unknown: { label: 'État inconnu', className: 'bg-gray-100 text-gray-500', dot: 'bg-gray-300' },
};

const AssociationCard = ({ association, onClick, onUpdate, onAssignOwner }) => {
  const [controlling, setControlling] = useState(false);
  const [isCurrentlyOn, setIsCurrentlyOn] = useState(false);
  const [loadingState, setLoadingState] = useState(false); // ✅ CHANGÉ À false
  const [showAcPanel, setShowAcPanel] = useState(false);
  // ✅ État du capteur en temps réel (Socket.io) — initialisé avec la valeur du dernier
  //    fetch REST (presenceValue calculé côté backend) puis mis à jour instantanément.
  const [presenceValue, setPresenceValue] = useState(association?.presenceValue || 'unknown');
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMounted = useRef(true);
  const fetchIntervalRef = useRef(null);

  // ✅ Le Contrôleur IR (climatisation) se pilote et se surveille très différemment
  //    d'un switch classique : pas de code Tuya "switch", pas de compteur d'énergie.
  const isIrController = association?.switchId?.type === 'ir_controller';

  // ✅ useCallback pour éviter les re-créations
  const fetchSwitchState = useCallback(async () => {
    if (!association?.switchId?.deviceId) {
      console.log('⚠️ Pas de deviceId pour:', association?.name);
      setLoadingState(false); // ✅ S'assurer que loading est false
      return;
    }

    if (!isMounted.current) return;

    // ✅ Contrôleur IR : l'état ON/OFF ne se lit pas sur /devices/:deviceId (ça
    //    renverrait le statut du hub, pas celui de la climatisation pilotée).
    //    On lit le statut réel de l'AC via l'endpoint dédié.
    if (isIrController) {
      try {
        setLoadingState(true);
        const response = await api.get(`/associations/${association._id}/ac-status`);
        const raw = response.data?.data || {};
        const isOn = raw.power === true || raw.power === '1' || raw.power === 1;
        if (isMounted.current) setIsCurrentlyOn(isOn);
      } catch (error) {
        console.error('❌ Erreur fetch statut AC:', error.message);
      } finally {
        if (isMounted.current) setLoadingState(false);
      }
      return;
    }

    try {
      setLoadingState(true);
      const timestamp = Date.now();
      
      console.log('🔍 [Card]', association.name, '- Fetch état');
      
      const response = await api.get(`/devices/${association.switchId.deviceId}?t=${timestamp}`);
      
      if (!response.data.success) {
        console.error('❌ Réponse invalide');
        setLoadingState(false);
        return;
      }

      const status = response.data.data.realtimeStatus;
      
      if (!Array.isArray(status)) {
        console.error('❌ Status invalide');
        setLoadingState(false);
        return;
      }

      const switchData = status.find(s => s.code === 'switch' || s.code === 'switch_1');
      
      if (!switchData) {
        console.warn('⚠️ Aucun switch trouvé');
        setLoadingState(false);
        return;
      }

      // ✅ Gestion des formats
      let isOn = false;
      
      if (typeof switchData.value === 'boolean') {
        isOn = switchData.value;
      } else if (typeof switchData.value === 'string') {
        isOn = switchData.value.toLowerCase() === 'on' || 
               switchData.value.toLowerCase() === 'true' ||
               switchData.value === '1';
      } else if (typeof switchData.value === 'number') {
        isOn = switchData.value === 1;
      }
      
      console.log('💡 [Card]', association.name, '→', isOn ? 'ON ✅' : 'OFF ⭕');
      
      if (isMounted.current) {
        setIsCurrentlyOn(isOn);
      }
    } catch (error) {
      console.error('❌ Erreur fetch:', error.message);
    } finally {
      if (isMounted.current) {
        setLoadingState(false); // ✅ TOUJOURS remettre à false
      }
    }
  }, [association?.switchId?.deviceId, association?.name, association?._id, isIrController]); // ✅ Dépendances correctes

  // ✅ Effet initial - récupération unique au montage
  useEffect(() => {
    console.log('🔄 [Card] Montage -', association.name);
    fetchSwitchState();
    
    return () => {
      isMounted.current = false;
    };
  }, []); // ✅ Vide = exécuté 1 seule fois au montage

  // ✅ Effet séparé pour l'auto-refresh — devient un simple filet de sécurité (30s)
  //    car Socket.io pousse désormais les changements instantanément (cf. effet ci-dessous).
  //    Ça réduit fortement les appels à l'API Tuya par rapport aux 15s précédents.
  useEffect(() => {
    if (fetchIntervalRef.current) {
      clearInterval(fetchIntervalRef.current);
    }

    fetchIntervalRef.current = setInterval(() => {
      fetchSwitchState();
    }, 30000);

    return () => {
      if (fetchIntervalRef.current) {
        clearInterval(fetchIntervalRef.current);
      }
    };
  }, [fetchSwitchState]); // ✅ Dépend de fetchSwitchState

  // ✅ Temps réel Socket.io — état du capteur (Presence/Move/None) ET état switch,
  //    exactement comme les boutons ON/OFF. La carte rejoint sa propre room et
  //    filtre les événements par associationId : aucune dépendance à ce que le
  //    parent (liste Admin/Owner) gère lui-même le socket.
  useEffect(() => {
    if (!association?._id) return;

    const socket = connectSocket();
    if (!socket) return;

    joinAssociation(association._id);

    const handleUpdate = (data) => {
      if (data.associationId !== association._id) return;
      if (data.presenceValue !== undefined) setPresenceValue(data.presenceValue);
      if (data.switchIsOn !== undefined && isMounted.current) {
        setIsCurrentlyOn(data.switchIsOn === true || data.switchIsOn === 'true' || data.switchIsOn === 1);
      }
    };

    const handleControl = (data) => {
      if (data.associationId !== association._id) return;
      if (data.switchState !== undefined) setIsCurrentlyOn(data.switchState === 'on');
    };

    const cleanupUpdate = onAssociationUpdate(handleUpdate);
    const cleanupControl = onAssociationControl(handleControl);

    return () => {
      cleanupUpdate();
      cleanupControl();
    };
  }, [association?._id]);

  const handleControl = async (action) => {
    if (controlling) {
      console.log('⚠️ Contrôle déjà en cours');
      return;
    }

    if (!association.switchId?.online) {
      alert('❌ Le switch est hors ligne');
      return;
    }

    console.log('🎮 [Card] Contrôle:', action, '-', association.name);
    
    setControlling(true);
    try {
      const response = await api.post(`/associations/${association._id}/control`, { action });
      
      console.log('✅ Réponse:', response.data);
      
      // ✅ PAS D'ALERT - juste une notification discrète
      console.log(`✅ Switch ${action === 'on' ? 'allumé' : 'éteint'}`);
      
      // ✅ Mettre à jour l'état immédiatement (optimiste)
      setIsCurrentlyOn(action === 'on');
      
      // ✅ Vérifier après 2 secondes
      setTimeout(() => {
        fetchSwitchState();
      }, 2000);
      
      onUpdate();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Erreur';
      console.error('❌ Erreur contrôle:', errorMsg);
      alert(`❌ ${errorMsg}`);
      
      // ✅ Re-vérifier l'état en cas d'erreur
      fetchSwitchState();
    } finally {
      setControlling(false);
    }
  };

  const handleToggleAutoMode = async () => {
    try {
      await api.patch(`/associations/${association._id}/auto-mode`, {
        enabled: !association.autoMode,
      });
      onUpdate();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur');
    }
  };

  // ✅ Calculer si les boutons doivent être désactivés
  const isDisabled = controlling || !association.active || !association.switchId?.online;

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-blue-400">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 sm:p-4">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base sm:text-lg truncate">{association.name}</h3>
            {association.description && (
              <p className="text-blue-100 text-xs sm:text-sm mt-1 line-clamp-2">
                {association.description}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end space-y-1 flex-shrink-0">
            {association.active ? (
              <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full whitespace-nowrap">
                Actif
              </span>
            ) : (
              <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full whitespace-nowrap">
                Inactif
              </span>
            )}
            {association.autoMode && (
              <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-full whitespace-nowrap">
                Auto
              </span>
            )}
            {isIrController && (
              <span className="px-2 py-1 bg-cyan-500 text-white text-xs rounded-full whitespace-nowrap">
                🌡️ Climatisation IR
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-3 sm:p-4">
        {/* Sensor */}
        <div className="mb-3 sm:mb-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <span className="text-xl sm:text-2xl flex-shrink-0">📡</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Capteur</p>
              <p className="font-medium text-gray-800 text-sm sm:text-base break-words">
                {association.sensorId?.name || 'N/A'}
              </p>
            </div>
            <div className="flex-shrink-0">
              {association.sensorId?.online ? (
                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full block"></span>
              ) : (
                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full block"></span>
              )}
            </div>
          </div>
          {/* ✅ État temps réel du capteur (Socket.io) */}
          <div className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${PRESENCE_LABELS[presenceValue]?.className || PRESENCE_LABELS.unknown.className}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${PRESENCE_LABELS[presenceValue]?.dot || PRESENCE_LABELS.unknown.dot}`}></span>
            {PRESENCE_LABELS[presenceValue]?.label || PRESENCE_LABELS.unknown.label}
          </div>
        </div>

        {/* Flèche */}
        <div className="flex justify-center mb-3 sm:mb-4">
          <span className="text-gray-400 text-xl sm:text-2xl">↓</span>
        </div>

        {/* Switch */}
        <div className="mb-3 sm:mb-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <span className="text-xl sm:text-2xl flex-shrink-0">{isIrController ? '🌡️' : '💡'}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500 uppercase font-semibold">
                  {isIrController ? 'Contrôleur IR' : 'Interrupteur'}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('🔄 Refresh manuel');
                    fetchSwitchState();
                  }}
                  disabled={loadingState}
                  className="text-blue-500 hover:text-blue-700 disabled:opacity-50 text-sm transition"
                  title="Rafraîchir"
                >
                  {loadingState ? '⏳' : '🔄'}
                </button>
              </div>
              <p className="font-medium text-gray-800 text-sm sm:text-base break-words">
                {association.switchId?.name || 'N/A'}
              </p>
              {/* ✅ État */}
              <p className={`text-xs font-bold mt-1 ${isCurrentlyOn ? 'text-green-600' : 'text-gray-500'}`}>
                {isCurrentlyOn ? '⚡ ALLUMÉ' : '⭕ ÉTEINT'}
              </p>
            </div>
            <div className="flex-shrink-0">
              {association.switchId?.online ? (
                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full block"></span>
              ) : (
                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full block"></span>
              )}
            </div>
          </div>
        </div>

        {/* Owner */}
        {association.ownerId ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
            <p className="text-xs text-green-800 font-semibold mb-1">👤 Owner assigné</p>
            <p className="font-medium text-green-900 text-sm truncate">{association.ownerId.name}</p>
            <p className="text-xs text-green-700 truncate">{association.ownerId.email}</p>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
            <p className="text-xs text-yellow-800 font-semibold mb-1">⚠️ Owner non assigné</p>
          </div>
        )}

        {/* Verrouillage */}
        {association.manualOverride?.enabled && (
          <div className="bg-orange-50 border border-orange-300 rounded-lg p-2 sm:p-3 mb-3">
            <p className="text-xs text-orange-800 font-semibold mb-1">🔒 Verrouillage actif</p>
            <p className="text-orange-700 text-sm">État: {association.manualOverride.state.toUpperCase()}</p>
          </div>
        )}

        {/* Stats */}
        <div className="bg-gray-50 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
            <div>
              <p className="text-gray-500 mb-1">Activations</p>
              <p className="font-bold text-blue-600 text-base sm:text-lg">
                {association.statistics?.totalActivations || 0}
              </p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">Dernière action</p>
              <p className="font-bold text-gray-700 text-xs">
                {association.lastAction?.type ? (
                  <span className="break-words">
                    {association.lastAction.type.replace('_', ' ').toUpperCase()}
                  </span>
                ) : (
                  'Aucune'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Programmation */}
        {association.schedule?.enabled && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
            <p className="text-xs text-yellow-800 font-semibold mb-2">📅 Programmé</p>
            {association.schedulePeriods?.length > 0 ? (
              <div className="space-y-1 text-xs sm:text-sm text-yellow-700">
                {association.schedulePeriods.map((p) => (
                  <div key={p._id} className="flex justify-between gap-2">
                    <span className="font-medium">{p.name}</span>
                    <span>{p.onTime} → {p.offTime}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2 text-xs sm:text-sm text-yellow-700">
                <span>ON: {association.schedule.onTime}</span>
                <span>OFF: {association.schedule.offTime}</span>
              </div>
            )}
          </div>
        )}

        {/* ✅ BOUTONS DE CONTRÔLE */}
        <div className="space-y-2">
          {/* ✅ ON / OFF */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleControl('on')}
              disabled={isDisabled}
              className={`px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg font-bold text-sm transition-all duration-300 transform hover:scale-105 ${
                isDisabled
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : isCurrentlyOn
                  ? 'bg-green-600 text-white shadow-lg shadow-green-500/50 border-2 border-green-400'
                  : 'bg-green-500 text-white hover:bg-green-600 hover:shadow-lg'
              }`}
            >
              {controlling ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span>
                </span>
              ) : isCurrentlyOn ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-pulse">✅</span>
                  <span>ON</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <span>🔆</span>
                  <span>ON</span>
                </span>
              )}
            </button>
            
            <button
              onClick={() => handleControl('off')}
              disabled={isDisabled}
              className={`px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg font-bold text-sm transition-all duration-300 transform hover:scale-105 ${
                isDisabled
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : !isCurrentlyOn
                  ? 'bg-gray-600 text-white shadow-lg shadow-gray-500/50 border-2 border-gray-400'
                  : 'bg-red-500 text-white hover:bg-red-600 hover:shadow-lg'
              }`}
            >
              {controlling ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span>
                </span>
              ) : !isCurrentlyOn ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-pulse">✅</span>
                  <span>OFF</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <span>🌙</span>
                  <span>OFF</span>
                </span>
              )}
            </button>
          </div>

          {/* Autres boutons */}
          <button
            onClick={handleToggleAutoMode}
            className={`w-full px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg transition font-semibold text-xs sm:text-sm ${
              association.autoMode
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {association.autoMode ? '🤖 Mode Auto: ON' : '🔧 Mode Auto: OFF'}
          </button>

          <button
            onClick={() => onClick(association)}
            className="w-full px-3 py-2 sm:px-4 sm:py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold text-xs sm:text-sm"
          >
            📊 Détails
          </button>

          {onAssignOwner && user?.role === ROLES.ADMIN && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAssignOwner(association);
              }}
              className="w-full px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition font-semibold text-xs sm:text-sm"
            >
              👤 Assigner Owner
            </button>
          )}

          {/* ✅ Climatisation IR : panneau dédié (température/mode/ventilateur) au lieu
              du Dashboard Consommation, non applicable (pas de mesure d'énergie sur un IR) */}
          {isIrController ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAcPanel(true);
              }}
              className="w-full px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-cyan-600 to-blue-700 text-white rounded-lg hover:from-cyan-700 hover:to-blue-800 transition font-semibold text-xs sm:text-sm"
            >
              🌡️ Piloter la climatisation
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/associations/${association._id}/dashboard`);
              }}
              className="w-full px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition font-semibold text-xs sm:text-sm"
            >
              📊 Dashboard Consommation
            </button>
          )}
        </div>
      </div>

      {showAcPanel && (
        <Modal isOpen={true} onClose={() => setShowAcPanel(false)} title={`🌡️ ${association.name}`}>
          <AcControlPanel associationId={association._id} associationName={association.name} />
        </Modal>
      )}
    </div>
  );
};

export default AssociationCard;